<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'TextTemplate',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const objectTypes = ref([
    {
      value: 'invoice',
      name: $fn.value.pgettext('TextTemplate|Type', 'Invoice'),
    },
    {
      value: 'invoice_line',
      name: $fn.value.pgettext('TextTemplate|Type', 'Invoice line'),
    },
  ]);
  const formId = ref(null);
  const formName = ref('textTemplate');
  const labelAlign = ref('right');
  const container = ref(null);
  const item = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    objectTypes: objectTypes.value,
    formId: formId.value,
    formName: formName.value,
    labelAlign: labelAlign.value,
    container: container.value,
    item: item.value,
    ux: ux.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_container = () => {
    return {
      self: $fn.value.urlFor('create_object_by_type', {
        object_type: 'text-templates',
      }),
      object: 'list',
      data: [],
    };
  };
  const $$load_item = () => {
    return props.oid
      ? $fn.value.fetch('get_object_by_type', {
          parameters: { object_type: 'text-templates', id: props.oid },
          asResource: true,
        })
      : new $fn.value.Resource({
          name: null,
          object_type: null,
          condition: null,
          template: null,
          description: null,
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'objectTypes' },
      { name: 'formId' },
      { name: 'formName' },
      { name: 'labelAlign' },
      { name: 'container' },
      { name: 'item' },
      { name: 'ux' },
    ],
    loaders: {
      formId: $$load_formId,
      container: $$load_container,
      item: $$load_item,
    },
    ux: {},
    dataRefs: {
      objectTypes,
      formId,
      formName,
      labelAlign,
      container,
      item,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  const act_2248226175642056_emit_haeiu7vk9_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data.response.data,
    };
  };
  const act_2248226175642056_rest_hxu1a0qc1_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: container.value,
      resource: item.value,
    };
  };

  const act_7315092382398562_reloadComponentData_4ectmd97v =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8oto99hc4 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_4ectmd97v,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8oto99hc4],
    event: 'reload',
  });
  const act_2248226175642056_emit_haeiu7vk9 = new actionTypes.EmitAction({
    actionArgs: act_2248226175642056_emit_haeiu7vk9_getActionArgs,
  });
  const inst_evh_2248226175642056_saved_92uoumaug =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_emit_haeiu7vk9,
      event: 'saved',
      displayName: 'emit',
    });
  const act_2248226175642056_rest_hxu1a0qc1 = new actionTypes.RestAction({
    actionArgs: act_2248226175642056_rest_hxu1a0qc1_getActionArgs,
    displayName: 'save',
    events: [inst_evh_2248226175642056_saved_92uoumaug],
  });
  const inst_evh_2248226175642056_submit_cgn83c4zj =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_rest_hxu1a0qc1,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_2248226175642056_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_submit_cgn83c4zj],
    event: 'submit',
  });

  const actions = {
    inst_evh_7315092382398562_reload_8oto99hc4,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_saved_92uoumaug,
    inst_evh_2248226175642056_submit_cgn83c4zj,
    save: inst_evh_2248226175642056_submit_cgn83c4zj,
    inst_evh_2248226175642056_submit,
  };

  const evh_7315092382398562_reload_8oto99hc4 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8oto99hc4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_saved_92uoumaug = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_saved_92uoumaug.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit_cgn83c4zj = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit_cgn83c4zj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_submit = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpForm
      :id="formId"
      :name="formName"
      :readonly="item.isReadOnly"
      class="row"
      v-if="loaded"
      @submit="evh_2248226175642056_submit($event, {})"
    >
      <ItpCol>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="delivery_modes"
          :label="pgettext('TextTemplate', 'Object type')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormSearchableSelect
            name="object_type"
            v-model="item.object_type"
            :options="objectTypes"
            valueField="value"
            textField="name"
            required
          >
          </ItpFormSearchableSelect>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="name"
          :label="pgettext('TextTemplate', 'Name')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormInput name="name" v-model="item.name" required>
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="description"
          :label="pgettext('TextTemplate', 'Description')"
          :labelAlign="labelAlign"
        >
          <ItpFormInput name="description" v-model="item.description">
          </ItpFormInput>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="condition"
          :label="pgettext('TextTemplate', 'Condition')"
          :labelAlign="labelAlign"
        >
          <ItpFormTextarea name="condition" v-model="item.condition">
          </ItpFormTextarea>
        </ItpFormGroup>
        <ItpFormGroup
          :label-cols-md="2"
          :content-cols-md="10"
          name="condition"
          :label="pgettext('TextTemplate', 'Template')"
          :labelAlign="labelAlign"
          required
        >
          <ItpFormTextarea
            name="template"
            monospace
            v-model="item.template"
            required
          >
          </ItpFormTextarea>
        </ItpFormGroup>
        <div>
          <ItpButton
            :text="pgettext('Button', 'Save')"
            type="submit"
            :form="formId"
            variant="primary"
            icon="fa-save"
            :spinning="actions.save && actions.save.isRunning"
            :disabled="!forms[formName] || !forms[formName].submittable"
            class="ms-auto"
          >
          </ItpButton>
        </div>
      </ItpCol>
    </ItpForm>
  </ItpUsercontrol>
</template>
